import React from 'react';

/**
 * Componente para formatar texto longo de forma legível
 * Detecta listas, sentenças e estrutura o texto em blocos organizados
 */
const FormattedText = ({ text, className = '' }) => {
  if (!text) return null;

  // Validação: garantir que text é uma string
  let textString = text;
  if (typeof text !== 'string') {
    // Se é array, junta com quebras de linha
    if (Array.isArray(text)) {
      textString = text.join('\n');
    } else if (typeof text === 'object') {
      // Se é objeto, tenta JSON.stringify
      try {
        textString = JSON.stringify(text, null, 2);
      } catch (e) {
        textString = String(text);
      }
    } else {
      // Converte para string
      textString = String(text);
    }
  }

  // Divide o texto em linhas
  const lines = textString.split('\n').filter(line => line.trim());

  const elements = [];
  let currentParagraph = [];
  let currentList = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const fullText = currentParagraph.join(' ');
      
      // Se o parágrafo é muito longo (>200 chars), tenta quebrar em sentenças
      if (fullText.length > 200) {
        // Quebra em sentenças (por ponto final seguido de espaço e maiúscula)
        const sentences = fullText.match(/[^.!?]+[.!?]+(?=\s+[A-ZÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÃÕÇ]|$)/g);
        
        if (sentences && sentences.length > 1) {
          elements.push({
            type: 'structured',
            sentences: sentences.map(s => s.trim())
          });
        } else {
          elements.push({
            type: 'paragraph',
            content: fullText
          });
        }
      } else {
        elements.push({
          type: 'paragraph',
          content: fullText
        });
      }
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push({
        type: 'list',
        items: [...currentList]
      });
      currentList = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Detecta itens de lista: começa com -, •, *, ou número seguido de ponto/parêntese
    const isListItem = /^[-•*]\s+/.test(trimmed) || /^\d+[.)]\s+/.test(trimmed);

    if (isListItem) {
      // Finaliza parágrafo anterior se houver
      flushParagraph();
      
      // Remove o marcador da lista e adiciona ao array
      const cleanText = trimmed.replace(/^[-•*]\s+/, '').replace(/^\d+[.)]\s+/, '');
      currentList.push(cleanText);
    } else {
      // Finaliza lista anterior se houver
      flushList();
      
      // Adiciona ao parágrafo atual
      currentParagraph.push(trimmed);
    }
  });

  // Finaliza qualquer conteúdo pendente
  flushParagraph();
  flushList();

  return (
    <div className={`space-y-4 ${className}`}>
      {elements.map((element, index) => {
        if (element.type === 'paragraph') {
          return (
            <p key={index} className="text-sm text-gray-800 leading-relaxed">
              {element.content}
            </p>
          );
        } else if (element.type === 'structured') {
          // Texto estruturado em sentenças separadas
          return (
            <div key={index} className="space-y-2">
              {element.sentences.map((sentence, sentIndex) => (
                <p key={sentIndex} className="text-sm text-gray-800 leading-relaxed pl-3 border-l-2 border-blue-200">
                  {sentence}
                </p>
              ))}
            </div>
          );
        } else if (element.type === 'list') {
          return (
            <ul key={index} className="space-y-2 ml-4">
              {element.items.map((item, itemIndex) => (
                <li key={itemIndex} className="text-sm text-gray-800 leading-relaxed flex">
                  <span className="text-blue-600 mr-2 flex-shrink-0">•</span>
                  <span className="flex-1">{item}</span>
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  );
};

export default FormattedText;
